import * as React from 'react';
import { connect } from 'react-redux';

import Page from 'components/page/Page';
import InfiniteScrollIndicator from 'components/infinite-load-indicator/InfiniteScrollIndicator';

import SharedAccessProductGrid from '../components/grid/SharedAccessProductGrid';
import SharedAccessEmptyList from '../components/empty-list/SharedAccessEmptyList';
import SharedAccessProductTile from '../components/product-tile/SharedAccessProductTile';
import SharedAccessProductListTitle from '../components/list-title/SharedAccessProductListTitle';
import SharedAccessProductFilters, { DEFAULT_VALUE } from '../components/product-filters/SharedAccessProductFilters';

import { fetchSharedAccessProducts, resetSharedAccessProductList } from '../reducers/sharedAccessProductList';
import { fetchSharedAccessFilters, resetSharedAccessProductFilters } from '../reducers/sharedAccessProductFilters';
import { fetchSharedAccessListInfo, resetSharedAccessProductListInfo } from '../reducers/sharedAccessProductListInfo';

import { __t } from 'utils/language';
import { renderPrice } from 'utils/helper/price';
import { getQueryValueFromUrl } from 'utils/url';
import { updateQueryParams } from 'utils/location';

import type { LocationWindow } from 'types';
import type {
  SharedAccessProductListFilters,
  SharedAccessProductListType
} from '../types/SharedAccessProductList.type';

type OwnProps = {};

type StateProps = {
  location: LocationWindow,
  title: string,
  products: Array<SharedAccessProductListType>,
  isLoading: boolean,
  currentPage: number,
  pagesTotal: number,
  filters: {
    categories: Array<string>,
    brands: Array<string>,
    colors: Array<string>
  }
};

type DispatchProps = {
  dispatchFetchProductList: (b: number, a: SharedAccessProductListFilters) => Promise<void>,
  dispatchResetProductList: () => Promise<void>,
  dispatchFetchProductFilters: () => Promise<void>,
  dispatchResetProductFilters: () => Promise<void>,
  dispatchFetchProductListInfo: () => Promise<void>,
  dispatchResetProductListInfo: () => Promise<void>
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  category?: string,
  brand?: string,
  color?: string
};

const QueryParam = {
  CATEGORY: 'category',
  BRAND: 'brand',
  COLOR: 'color'
};


// since we want either string or undefined
function getFilterValue(value: string | undefined): string | undefined {
  if (value === DEFAULT_VALUE) {
    return undefined;
  }

  return value;
}

class SharedAccessOutfitsPage extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      category: getQueryValueFromUrl(props.location, QueryParam.CATEGORY) || undefined,
      brand: getQueryValueFromUrl(props.location, QueryParam.BRAND) || undefined,
      color: getQueryValueFromUrl(props.location, QueryParam.COLOR) || undefined
    };
  }

  componentDidMount(): void {
    const {
      dispatchFetchProductFilters,
      dispatchFetchProductListInfo
    } = this.props;

    this.refetchData();
    dispatchFetchProductFilters();
    dispatchFetchProductListInfo();
  }

  componentWillUnmount(): void {
    const {
      dispatchResetProductList,
      dispatchResetProductFilters,
      dispatchResetProductListInfo
    } = this.props;

    dispatchResetProductList();
    dispatchResetProductFilters();
    dispatchResetProductListInfo();
  }

  refetchData() {
    const { dispatchFetchProductList } = this.props;
    dispatchFetchProductList(1, this.getFilters());
  }

  getFilters(): SharedAccessProductListFilters {
    const { category, brand, color } = this.state;

    return {
      category: getFilterValue(category),
      color: getFilterValue(color),
      brandName: getFilterValue(brand)
    };
  }

  handleCategoryChange = (newValue: string): void => {
    const { location } = this.props;

    updateQueryParams(location, {
      [QueryParam.CATEGORY]: getFilterValue(newValue)
    });
    this.setState({
      category: newValue
    }, this.refetchData);
  };

  handleBrandChange = (newValue: string): void => {
    const { location } = this.props;

    updateQueryParams(location, {
      [QueryParam.BRAND]: getFilterValue(newValue)
    });
    this.setState({
      brand: newValue
    }, this.refetchData);
  };

  handleColorChange = (newValue: string): void => {
    const { location } = this.props;

    updateQueryParams(location, {
      [QueryParam.COLOR]: getFilterValue(newValue)
    });
    this.setState({
      color: newValue
    }, this.refetchData);
  };

  handleLoadMore = (): void => {
    const { currentPage, dispatchFetchProductList } = this.props;

    dispatchFetchProductList(currentPage + 1, this.getFilters());
  };

  renderFilters(): React.ReactNode {
    const { filters } = this.props;
    const { category, color, brand } = this.state;

    return (
      <SharedAccessProductFilters
        categories={filters.categories}
        brands={filters.brands}
        colors={filters.colors}
        currentBrand={brand || DEFAULT_VALUE}
        currentCategory={category || DEFAULT_VALUE}
        currentColor={color || DEFAULT_VALUE}
        onCategoryChange={this.handleCategoryChange}
        onBrandChange={this.handleBrandChange}
        onColorChange={this.handleColorChange}
      />
    );
  }

  renderTiles(): React.ReactNode {
    const { products, isLoading } = this.props;

    if (!isLoading && products.length === 0) {
      return (
        <SharedAccessEmptyList />
      );
    }

    return (
      <SharedAccessProductGrid>
        {products.map((entity, index) => (
          <SharedAccessProductTile
            key={`product-tile-${index}-${entity.name}`}
            brand={entity.brandName}
            name={entity.name}
            imageUrl={entity.imageUrl}
            itemUrl={entity.itemUrl}
            price={renderPrice(entity.price)}
          />
        ))}
      </SharedAccessProductGrid>
    );
  }

  render(): React.ReactNode {
    const { isLoading, currentPage, pagesTotal, title } = this.props;
    const hasMorePages = currentPage < pagesTotal;

    return (
      <Page
        documentTitle={__t('shared_access_product_list_page_title')}
      >
        <SharedAccessProductListTitle title={title} />
        {this.renderFilters()}
        {this.renderTiles()}
        {hasMorePages && (
        <InfiniteScrollIndicator
          detectionOffset={300}
          isLoading={isLoading}
          onLoadMore={this.handleLoadMore}
        />
        )}
      </Page>
    );
  }
}

function mapStateToProps(
  {
    sharedAccessProductList,
    sharedAccessProductFilters,
    sharedAccessProductListInfo,
    router
  }
): StateProps {
  return {
    location: router.location,
    title: sharedAccessProductListInfo.title,
    products: sharedAccessProductList.products,
    currentPage: sharedAccessProductList.currentPage,
    pagesTotal: sharedAccessProductList.pagesTotal,
    isLoading: sharedAccessProductList.isLoading,
    filters: {
      categories: sharedAccessProductFilters.categories,
      brands: sharedAccessProductFilters.brands,
      colors: sharedAccessProductFilters.colors
    }
  };
}

function mapDispatchToProps(dispatch): DispatchProps {
  return {
    dispatchFetchProductList: (page: number, filters: SharedAccessProductListFilters) =>
      dispatch(fetchSharedAccessProducts(page, filters)),
    dispatchResetProductList: () => dispatch(resetSharedAccessProductList()),
    dispatchFetchProductFilters: () => dispatch(fetchSharedAccessFilters()),
    dispatchResetProductFilters: () => dispatch(resetSharedAccessProductFilters()),
    dispatchFetchProductListInfo: () => dispatch(fetchSharedAccessListInfo()),
    dispatchResetProductListInfo: () => dispatch(resetSharedAccessProductListInfo())
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(SharedAccessOutfitsPage);
